import styled from 'styled-components';
import { screenXsMax, gray30 } from '@spotify-internal/encore-web';

interface StickyStyleProps {
  stickyHeader: boolean;
}

type BylineProps = {
  alwaysShow: boolean;
};

const Container = styled.div<StickyStyleProps>`
  width: 100%;
  margin-bottom: ${(props) => (props.stickyHeader ? 0 : '4px')};
`;

const EntityType = styled.div`
  line-height: 1.33;
  color: rgba(0, 0, 0, 0.4);
  font-weight: 700;
  font-size: 14px;

  @media (max-width: ${screenXsMax}) {
    line-height: 1.67;
  }
`;

const Title = styled.h1<StickyStyleProps>`
  padding-right: 80px;
  font-size: 32px;
  font-weight: 900;
  line-height: 36px;
  letter-spacing: -0.25px;

  ${(props) =>
    props.stickyHeader
      ? `
      font-size: 18px;
      line-height: 1.44;
      letter-spacing: -0.3px;
    `
      : `
      font-size: 32px;
      line-height: 36px;
      letter-spacing: -0.25px;
      padding-bottom: 4px;
    `}

  @media (max-width: ${screenXsMax}) {
    font-size: 16px;
    line-height: 1.5;
    letter-spacing: normal;
    padding-right: 16px;
  }
`;

const Byline = styled.div<BylineProps>`
  letter-spacing: 0.3px;
  font-size: 14px;
  color: ${gray30};
  margin-bottom: 14px;

  ${(props) =>
    !props.alwaysShow
      ? `
      @media (max-width: ${screenXsMax}) {
        display: none;
      } 
    `
      : null}
`;

type EntityTitleProps = {
  /** Text for the entity type that appears on top, e.g. SONG, WORK, SONGWRITER */
  type: React.ReactNode;
  /** Title of the entity */
  title: React.ReactNode;
  /** Smaller gray text beneath the title, often used for the author of the entity */
  byline: React.ReactNode;
  /** Boolean triggering sticky functionality. */
  stickyHeader: boolean;
  className?: string;
  alwaysShowByline?: boolean;
};

export default function EntityTitle({
  type,
  title,
  byline,
  stickyHeader,
  className,
  alwaysShowByline = false,
  ...otherProps
}: EntityTitleProps) {
  return (
    <Container stickyHeader={stickyHeader} className={className} {...otherProps}>
      <EntityType>{type}</EntityType>
      <Title stickyHeader={stickyHeader}>{title}</Title>
      {!stickyHeader && <Byline alwaysShow={alwaysShowByline}>{byline}</Byline>}
    </Container>
  );
}
